import { useEffect, useMemo, useState } from "react";
import {
  benchmarkById,
  benchmarks,
  claims,
  dataThrough,
  labById,
  modelById,
  models,
  prices,
  sourceById,
  sources,
} from "./data/v1";
import {
  calculateWorkloadCost,
  formatUsd,
  getCrossSourcePairs,
  isOwnerReported,
  reporterName,
} from "./lib/compare";
import type { BenchmarkId, Claim, Source, Workload } from "./types";
import { CompassMark, Icon } from "./components/Icons";
import { ChartCrimesView } from "./components/ChartCrimesView";
import { LabLogo } from "./components/LabLogo";
import { ReleaseChartsView } from "./components/ReleaseChartsView";
import { SearchDialog, type SearchSelection } from "./components/SearchDialog";
import { chartCrimes } from "./data/chartCrimes";

type ViewId = "releases" | "benchmarks" | "pricing" | "sources" | "chart-crimes" | "methodology";

interface FocusTarget {
  revision: number;
  benchmarkId?: BenchmarkId;
  modelId?: string;
  sourceId?: string;
}

const viewLabels: Record<ViewId, string> = {
  releases: "Release charts",
  benchmarks: "Benchmarks",
  pricing: "Pricing",
  sources: "Source claims",
  "chart-crimes": "Chart crimes",
  methodology: "Methodology",
};

const viewIcons: Record<ViewId, "bars" | "compare" | "pricing" | "source" | "warning" | "book"> = {
  releases: "bars",
  benchmarks: "compare",
  pricing: "pricing",
  sources: "source",
  "chart-crimes": "warning",
  methodology: "book",
};

const viewFromHash = (): ViewId | null => {
  const candidate = window.location.hash.slice(1);
  if (!candidate) return "releases";
  return candidate in viewLabels ? candidate as ViewId : null;
};

function App() {
  const [view, setView] = useState<ViewId>(() => viewFromHash() ?? "releases");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState<FocusTarget>({ revision: 0 });

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((current) => !current);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const handleHistory = () => {
      const nextView = viewFromHash();
      if (nextView) setView(nextView);
    };
    window.addEventListener("hashchange", handleHistory);
    window.addEventListener("popstate", handleHistory);
    return () => {
      window.removeEventListener("hashchange", handleHistory);
      window.removeEventListener("popstate", handleHistory);
    };
  }, []);

  const navigate = (nextView: ViewId) => {
    setView(nextView);
    setMobileNavOpen(false);
    const nextUrl = nextView === "releases"
      ? `${window.location.pathname}${window.location.search}`
      : `${window.location.pathname}${window.location.search}#${nextView}`;
    window.history.pushState(null, "", nextUrl);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchSelection = (selection: SearchSelection) => {
    const revision = focusTarget.revision + 1;
    if (selection.kind === "pricing") {
      setView("pricing");
      setFocusTarget({ revision, modelId: selection.id });
    } else if (selection.kind === "benchmark") {
      setView("benchmarks");
      setFocusTarget({ revision, benchmarkId: selection.id });
    } else if (selection.kind === "model") {
      setView("benchmarks");
      setFocusTarget({ revision, modelId: selection.id });
    } else {
      setView("sources");
      setFocusTarget({ revision, sourceId: selection.id });
    }
  };

  const openBenchmark = (benchmarkId: BenchmarkId) => {
    setView("benchmarks");
    setFocusTarget({
      revision: focusTarget.revision + 1,
      benchmarkId,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to comparison</a>
      <header className="site-header">
        <div className="site-header__inner">
          <button className="wordmark" onClick={() => navigate("releases")} aria-label="ModelScout home">
            <span>ModelScout</span>
            <CompassMark />
          </button>
          <nav aria-label="Primary navigation" className={mobileNavOpen ? "primary-nav is-open" : "primary-nav"}>
            {(Object.keys(viewLabels) as ViewId[]).map((viewId) => (
              <button
                aria-current={view === viewId ? "page" : undefined}
                className="nav-link"
                key={viewId}
                onClick={() => navigate(viewId)}
              >
                <Icon name={viewIcons[viewId]} size={17} />
                {viewLabels[viewId]}
                {viewId === "sources" && <small>{getCrossSourcePairs(claims).length}</small>}
              </button>
            ))}
          </nav>
          <button
            aria-label="Search models, benchmarks, sources, and prices"
            className="search-pill"
            onClick={() => setSearchOpen(true)}
          >
            <Icon name="search" size={17} />
            <span>Search</span>
            <kbd>Ctrl K</kbd>
          </button>
          <div className="utility-meta">
            <span>{dataThrough}</span>
          </div>
          <button
            aria-expanded={mobileNavOpen}
            aria-label="Toggle navigation"
            className="mobile-menu"
            onClick={() => setMobileNavOpen((current) => !current)}
          >
            <Icon name={mobileNavOpen ? "close" : "menu"} />
          </button>
        </div>
      </header>

      <main id="main-content">
        <section className={view === "releases" ? "intro intro--release" : "intro"}>
          <div className="intro__copy">
            <h1>{viewLabels[view]}</h1>
          </div>
          {view === "chart-crimes" ? (
            <dl className="coverage-strip" aria-label="Chart crime coverage">
              <div>
                <dt>Findings</dt><dd>{chartCrimes.length}</dd>
              </div>
              <div>
                <dt>Visual</dt><dd>{chartCrimes.filter((crime) => crime.group === "Visual error").length}</dd>
              </div>
              <div>
                <dt>Comparison</dt><dd>{chartCrimes.filter((crime) => crime.group === "Comparison flag").length}</dd>
              </div>
              <div>
                <dt>Labs</dt><dd>{new Set(chartCrimes.map((crime) => crime.lab)).size}</dd>
              </div>
            </dl>
          ) : (
            <dl className="coverage-strip" aria-label="Current data coverage">
              <div>
                <dt>Claims</dt><dd>{claims.length}</dd>
              </div>
              <div>
                <dt>Models</dt><dd>{models.length}</dd>
              </div>
              <div>
                <dt>Benchmarks</dt><dd>{benchmarks.length}</dd>
              </div>
              <div>
                <dt>Sources</dt><dd>{sources.length}</dd>
              </div>
            </dl>
          )}
        </section>

        {view === "releases" && <ReleaseChartsView onCompare={openBenchmark} />}
        {view === "benchmarks" && <BenchmarksView focusTarget={focusTarget} />}
        {view === "pricing" && <PricingView focusTarget={focusTarget} />}
        {view === "sources" && <SourcesView focusTarget={focusTarget} />}
        {view === "chart-crimes" && <ChartCrimesView />}
        {view === "methodology" && <MethodologyView />}
      </main>

      <SearchDialog
        onClose={() => setSearchOpen(false)}
        onSelect={handleSearchSelection}
        open={searchOpen}
      />
    </>
  );
}

function BenchmarksView({ focusTarget }: { focusTarget: FocusTarget }) {
  const [benchmarkId, setBenchmarkId] = useState<BenchmarkId>("swe-bench-pro");
  const [sourceId, setSourceId] = useState("xai-grok-45");
  const [selectedClaimId, setSelectedClaimId] = useState<string>("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState("all");

  const availableSources = useMemo(
    () =>
      sources.filter((source) =>
        claims.some((claim) => claim.benchmarkId === benchmarkId && claim.sourceId === source.id),
      ),
    [benchmarkId],
  );

  useEffect(() => {
    if (!availableSources.some((source) => source.id === sourceId)) {
      setSourceId(availableSources[0]?.id ?? "");
    }
  }, [availableSources, sourceId]);

  useEffect(() => {
    if (focusTarget.benchmarkId) {
      setBenchmarkId(focusTarget.benchmarkId);
      const firstSource = claims.find((claim) => claim.benchmarkId === focusTarget.benchmarkId)?.sourceId;
      if (firstSource) setSourceId(firstSource);
    }
    if (focusTarget.modelId) {
      const targetClaim = claims.find(
        (claim) => claim.modelId === focusTarget.modelId && claim.benchmarkId === benchmarkId,
      ) ?? claims.find((claim) => claim.modelId === focusTarget.modelId);
      if (targetClaim) {
        setBenchmarkId(targetClaim.benchmarkId);
        setSourceId(targetClaim.sourceId);
        setSelectedClaimId(targetClaim.id);
      }
    }
  }, [focusTarget.revision]);

  const chartClaims = useMemo(
    () =>
      claims
        .filter(
          (claim) =>
            claim.benchmarkId === benchmarkId &&
            claim.sourceId === sourceId &&
            (ownerFilter === "all" || modelById.get(claim.modelId)?.ownerId === ownerFilter),
        )
        .sort((a, b) =>
          benchmarkById.get(benchmarkId)?.direction === "lower"
            ? a.score - b.score
            : b.score - a.score,
        ),
    [benchmarkId, sourceId, ownerFilter],
  );

  useEffect(() => {
    if (!chartClaims.some((claim) => claim.id === selectedClaimId)) {
      setSelectedClaimId(chartClaims[0]?.id ?? "");
    }
  }, [chartClaims, selectedClaimId]);

  const selectedClaim = chartClaims.find((claim) => claim.id === selectedClaimId) ?? chartClaims[0];
  const benchmark = benchmarkById.get(benchmarkId)!;
  const source = sourceById.get(sourceId);
  const highestScore = Math.max(...chartClaims.map((claim) => claim.score), 1);
  const axisMaximum =
    benchmark.unit === "%"
      ? 100
      : benchmark.unit === "Elo"
        ? Math.ceil(highestScore / 200) * 200
        : Math.ceil(highestScore / 10_000) * 10_000;
  const axisTicks = Array.from({ length: 6 }, (_, index) => (axisMaximum / 5) * index);
  const formatAxisTick = (value: number) =>
    benchmark.unit === "tokens" && value >= 1_000
      ? `${Math.round(value / 1_000)}k`
      : new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
  const ownerOptions = Array.from(
    new Set(
      claims
        .filter((claim) => claim.benchmarkId === benchmarkId && claim.sourceId === sourceId)
        .map((claim) => modelById.get(claim.modelId)?.ownerId)
        .filter(Boolean),
    ),
  );

  return (
    <section className="workbench" aria-labelledby="benchmark-title">
      <aside className={filtersOpen ? "filter-rail is-open" : "filter-rail"}>
        <div className="mobile-sheet-head">
          <strong>Comparison filters</strong>
          <button className="icon-button" onClick={() => setFiltersOpen(false)} aria-label="Close filters">
            <Icon name="close" />
          </button>
        </div>
        <label className="field">
          <span>Benchmark</span>
          <select
            value={benchmarkId}
            onChange={(event) => {
              setBenchmarkId(event.target.value as BenchmarkId);
              setOwnerFilter("all");
            }}
          >
            {benchmarks.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Reporting source</span>
          <select value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
            {availableSources.map((item) => (
              <option key={item.id} value={item.id}>
                {labById.get(item.publisherId)?.shortName} · {item.publishedAt}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="owner-filter">
          <legend>Model owner</legend>
          <button
            aria-pressed={ownerFilter === "all"}
            onClick={() => setOwnerFilter("all")}
          >
            <span>All owners</span><small>{ownerOptions.length}</small>
          </button>
          {ownerOptions.map((ownerId) => (
            <button
              aria-pressed={ownerFilter === ownerId}
              key={ownerId}
              onClick={() => setOwnerFilter(ownerId ?? "all")}
            >
              <span>{ownerId ? labById.get(ownerId)?.shortName : "Unknown"}</span>
              <small>{claims.filter((claim) => claim.benchmarkId === benchmarkId && claim.sourceId === sourceId && modelById.get(claim.modelId)?.ownerId === ownerId).length}</small>
            </button>
          ))}
        </fieldset>
      </aside>

      <div className="workbench__main">
        <div className="workbench__toolbar">
          <button className="filter-trigger" onClick={() => setFiltersOpen(true)}>
            <Icon name="filter" size={17} /> Filters
            <span>{ownerFilter === "all" ? 2 : 3}</span>
          </button>
          <div className="toolbar-selects">
            <label>
              <span>Benchmark</span>
              <select value={benchmarkId} onChange={(event) => setBenchmarkId(event.target.value as BenchmarkId)}>
                {benchmarks.map((item) => <option key={item.id} value={item.id}>{item.shortName}</option>)}
              </select>
            </label>
            <label>
              <span>Reporter</span>
              <select value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
                {availableSources.map((item) => (
                  <option key={item.id} value={item.id}>{labById.get(item.publisherId)?.shortName}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="chart-head">
          <div>
            <p>{benchmark.category} · {benchmark.metric}</p>
            <h2 id="benchmark-title">{benchmark.name}</h2>
          </div>
          <div className="chart-direction">
            <Icon name="arrow" size={16} />
            {benchmark.direction === "higher" ? "Higher" : "Lower"} is better
          </div>
        </div>

        <div className="axis" aria-hidden="true">
          {axisTicks.map((tick) => <span key={tick}>{formatAxisTick(tick)}</span>)}
        </div>
        <div className="bar-chart" role="list" aria-label={`${benchmark.name} scores reported by ${source ? labById.get(source.publisherId)?.name : "source"}`}>
          {chartClaims.map((claim) => {
            const model = modelById.get(claim.modelId)!;
            const owner = labById.get(model.ownerId);
            const ownerReported = isOwnerReported(claim);
            return (
              <button
                aria-current={selectedClaim?.id === claim.id}
                className="bar-row"
                key={claim.id}
                onClick={() => setSelectedClaimId(claim.id)}
                role="listitem"
              >
                <span className="bar-row__model">
                  <LabLogo className="bar-row__logo" labId={model.ownerId} />
                  <span>
                    <strong>{model.name}</strong>
                    <small>{owner?.shortName}</small>
                  </span>
                </span>
                <span className="bar-row__plot">
                  <span
                    className={ownerReported ? "bar is-owner" : "bar"}
                    style={{ "--score": (claim.score / axisMaximum) * 100 } as React.CSSProperties}
                  />
                </span>
                <strong className="bar-row__score">{claim.displayValue}</strong>
                <span className={ownerReported ? "claim-tag is-owner" : "claim-tag"}>
                  {ownerReported ? "Owner" : "Competitor"}
                </span>
              </button>
            );
          })}
          {!chartClaims.length && <p className="empty-state">No claims match this owner filter.</p>}
        </div>
      </div>

      <EvidencePanel claim={selectedClaim} source={source} />

      <div className="claim-table-wrap">
        <div className="table-head">
          <div>
            <h3>{source?.title}</h3>
          </div>
          {source && (
            <a className="typographic-link" href={source.url} rel="noreferrer" target="_blank">
              Source <Icon name="external" size={15} />
            </a>
          )}
        </div>
        <table className="claim-table">
          <thead>
            <tr>
              <th>Model</th><th>Owner</th><th>Score</th><th>Reporter</th><th>Claim type</th><th>Locator</th>
            </tr>
          </thead>
          <tbody>
            {chartClaims.map((claim) => {
              const model = modelById.get(claim.modelId)!;
              const owner = labById.get(model.ownerId);
              return (
                <tr key={claim.id} onClick={() => setSelectedClaimId(claim.id)}>
                  <th>{model.name}</th>
                  <td>{owner?.shortName}</td>
                  <td className="numeric">{claim.displayValue}</td>
                  <td>{reporterName(claim)}</td>
                  <td><span className={isOwnerReported(claim) ? "claim-tag is-owner" : "claim-tag"}>{isOwnerReported(claim) ? "Owner" : "Competitor"}</span></td>
                  <td>{claim.locator}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="claim-cards">
          {chartClaims.map((claim) => {
            const model = modelById.get(claim.modelId)!;
            return (
              <button className="claim-card" key={claim.id} onClick={() => setSelectedClaimId(claim.id)}>
                <span><strong>{model.name}</strong><small>{labById.get(model.ownerId)?.shortName} · reported by {reporterName(claim)}</small></span>
                <b>{claim.displayValue}</b>
              </button>
            );
          })}
        </div>
      </div>
      {filtersOpen && <button className="sheet-backdrop" aria-label="Close filters" onClick={() => setFiltersOpen(false)} />}
    </section>
  );
}

function EvidencePanel({ claim, source }: { claim?: Claim; source?: Source }) {
  if (!claim || !source) return <aside className="evidence-panel"><p>No claim selected.</p></aside>;
  const model = modelById.get(claim.modelId)!;
  const owner = labById.get(model.ownerId)!;
  const publisher = labById.get(source.publisherId)!;
  const unit = benchmarkById.get(claim.benchmarkId)?.unit;
  const observationUnit = unit === "%" ? "%" : unit === "Elo" ? " Elo" : unit === "tokens" ? " tokens" : "";
  const otherClaims = claims.filter(
    (candidate) =>
      candidate.modelId === claim.modelId &&
      candidate.benchmarkId === claim.benchmarkId &&
      candidate.sourceId !== claim.sourceId,
  );
  const maxDelta = otherClaims.length
    ? Math.max(...otherClaims.map((candidate) => Math.abs(candidate.score - claim.score)))
    : null;

  return (
    <aside className="evidence-panel" aria-label="Selected claim evidence">
      <div className="evidence-panel__title">
        <span>Source</span>
        <Icon name="source" />
      </div>
      <section>
        <a href={source.url} rel="noreferrer" target="_blank">
          {source.title} <Icon name="external" size={14} />
        </a>
        <small>{source.publishedAt}</small>
      </section>
      <section className="evidence-grid">
        <div>
          <p>Reporter</p>
          <strong>{publisher.name}</strong>
        </div>
        <div>
          <p>Owner</p>
          <strong>{owner.name}</strong>
        </div>
      </section>
      <section>
        <p>Score</p>
        <div className="observation">
          <strong>{model.name}</strong>
          <b>{claim.displayValue}{observationUnit}</b>
        </div>
        <small>{claim.locator}</small>
      </section>
      {otherClaims.length > 0 ? (
        <section className={maxDelta && maxDelta > 0 ? "discrepancy is-different" : "discrepancy"}>
          <div>
            <Icon name={maxDelta && maxDelta > 0 ? "warning" : "check"} size={17} />
            <strong>{maxDelta === 0 ? "Match" : "Difference"}</strong>
          </div>
          <p>
            {otherClaims.map((candidate) => `${reporterName(candidate)} ${candidate.displayValue}`).join(" · ")}
            {maxDelta !== null && ` · max Δ ${maxDelta.toFixed(1)} pts`}
          </p>
        </section>
      ) : (
        <section className="discrepancy">
          <div><Icon name="info" size={17} /><strong>Single source</strong></div>
        </section>
      )}
    </aside>
  );
}

function PricingView({ focusTarget }: { focusTarget: FocusTarget }) {
  const [workload, setWorkload] = useState<Workload>({
    inputTokens: 100_000,
    cachedPercent: 25,
    outputTokens: 20_000,
    webSearches: 0,
  });
  const [highlightedModelId, setHighlightedModelId] = useState("");

  useEffect(() => {
    if (focusTarget.modelId && prices.some((price) => price.modelId === focusTarget.modelId)) {
      setHighlightedModelId(focusTarget.modelId);
      window.setTimeout(() => document.getElementById(`price-${focusTarget.modelId}`)?.scrollIntoView({ block: "center" }), 30);
    }
  }, [focusTarget.revision]);

  const ranked = useMemo(
    () =>
      prices
        .map((price) => ({ price, total: calculateWorkloadCost(price, workload) }))
        .sort((a, b) => a.total - b.total),
    [workload],
  );

  const updateNumber = (field: keyof Workload, value: string) => {
    const parsed = Number(value);
    setWorkload((current) => ({ ...current, [field]: Number.isFinite(parsed) ? parsed : 0 }));
  };

  return (
    <section className="pricing-view">
      <div className="pricing-layout">
        <form className="calculator" onSubmit={(event) => event.preventDefault()}>
          <div className="calculator__head">
            <strong>Workload</strong>
            <Icon name="compare" />
          </div>
          <label className="number-field">
            <span>Input tokens</span>
            <input min="0" step="1000" type="number" value={workload.inputTokens} onChange={(event) => updateNumber("inputTokens", event.target.value)} />
          </label>
          <label className="number-field">
            <span>Cached input</span>
            <span className="number-field__input"><input min="0" max="100" type="number" value={workload.cachedPercent} onChange={(event) => updateNumber("cachedPercent", event.target.value)} /><i>%</i></span>
          </label>
          <label className="number-field">
            <span>Output tokens</span>
            <input min="0" step="1000" type="number" value={workload.outputTokens} onChange={(event) => updateNumber("outputTokens", event.target.value)} />
          </label>
          <label className="number-field">
            <span>Search queries</span>
            <input min="0" step="1" type="number" value={workload.webSearches} onChange={(event) => updateNumber("webSearches", event.target.value)} />
          </label>
        </form>

        <div className="cost-ranking" aria-live="polite">
          <div className="cost-ranking__head">
            <span>Cost / request</span>
            <small>USD</small>
          </div>
          {ranked.map(({ price, total }, index) => {
            const model = modelById.get(price.modelId)!;
            const source = sourceById.get(price.sourceId)!;
            return (
              <article
                className={highlightedModelId === price.modelId ? "cost-row is-highlighted" : "cost-row"}
                id={`price-${price.modelId}`}
                key={price.modelId}
              >
                <span className="cost-row__rank">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <strong>{model.name}</strong>
                  <small>{labById.get(model.ownerId)?.name} · {price.condition}</small>
                </div>
                <b>{formatUsd(total)}</b>
                <a aria-label={`Open official pricing for ${model.name}`} href={source.url} rel="noreferrer" target="_blank">
                  <Icon name="external" size={16} />
                </a>
              </article>
            );
          })}
        </div>
      </div>

      <div className="price-sheet">
        <div className="table-head">
          <h3>USD / 1M tokens</h3>
          <span>{dataThrough}</span>
        </div>
        <table>
          <thead>
            <tr><th>Model</th><th>Input</th><th>Cache read</th><th>5m cache write</th><th>1h cache write</th><th>Output</th><th>Search / 1K</th><th>Condition</th><th>Source</th></tr>
          </thead>
          <tbody>
            {prices.map((price) => {
              const model = modelById.get(price.modelId)!;
              const source = sourceById.get(price.sourceId)!;
              return (
                <tr key={price.modelId}>
                  <th>{model.name}</th>
                  <td className="numeric">${price.input.toFixed(2)}</td>
                  <td className="numeric">{price.cachedInput === undefined ? "—" : `$${price.cachedInput.toFixed(2)}`}</td>
                  <td className="numeric">{price.cacheWrite5m === undefined ? "—" : `$${price.cacheWrite5m.toFixed(2)}`}</td>
                  <td className="numeric">{price.cacheWrite1h === undefined ? "—" : `$${price.cacheWrite1h.toFixed(2)}`}</td>
                  <td className="numeric">${price.output.toFixed(2)}</td>
                  <td className="numeric">{price.webSearchPerThousand === undefined ? "—" : `$${price.webSearchPerThousand.toFixed(2)}`}</td>
                  <td>{price.condition}</td>
                  <td><a href={source.url} rel="noreferrer" target="_blank">{labById.get(source.publisherId)?.shortName} <Icon name="external" size={13} /></a></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SourcesView({ focusTarget }: { focusTarget: FocusTarget }) {
  const pairs = getCrossSourcePairs(claims);
  const [selectedSourceId, setSelectedSourceId] = useState("");

  useEffect(() => {
    if (focusTarget.sourceId) setSelectedSourceId(focusTarget.sourceId);
  }, [focusTarget.revision]);

  const differing = pairs.filter((pair) => Math.abs(pair.delta) > 0);
  const matching = pairs.filter((pair) => pair.delta === 0);

  return (
    <section className="sources-view">
      <div className="source-summary">
        <article>
          <span>Exact matches</span><strong>{matching.length}</strong>
        </article>
        <article>
          <span>Different values</span><strong>{differing.length}</strong>
        </article>
        <article>
          <span>Largest delta</span><strong>{Math.max(...differing.map((pair) => Math.abs(pair.delta))).toFixed(1)} pts</strong>
        </article>
      </div>

      <div className="disagreement-sheet">
        <div className="table-head">
          <h3>Disagreements</h3>
          <span>{pairs.length}</span>
        </div>
        <table>
          <thead>
            <tr><th>Benchmark</th><th>Model</th><th>Reporter A</th><th>Score A</th><th>Reporter B</th><th>Score B</th><th>Δ</th><th>Review</th></tr>
          </thead>
          <tbody>
            {[...differing, ...matching].map((pair) => {
              const firstSource = sourceById.get(pair.first.sourceId)!;
              const secondSource = sourceById.get(pair.second.sourceId)!;
              return (
                <tr key={`${pair.first.id}:${pair.second.id}`}>
                  <th>{benchmarkById.get(pair.benchmarkId as BenchmarkId)?.shortName}</th>
                  <td>{modelById.get(pair.modelId)?.name}</td>
                  <td><a href={firstSource.url} rel="noreferrer" target="_blank">{reporterName(pair.first)} <Icon name="external" size={12} /></a></td>
                  <td className="numeric">{pair.first.displayValue}</td>
                  <td><a href={secondSource.url} rel="noreferrer" target="_blank">{reporterName(pair.second)} <Icon name="external" size={12} /></a></td>
                  <td className="numeric">{pair.second.displayValue}</td>
                  <td className={pair.delta === 0 ? "numeric delta-zero" : "numeric delta-different"}>{pair.delta > 0 ? "+" : ""}{pair.delta.toFixed(1)}</td>
                  <td><span className={pair.delta === 0 ? "review-tag is-match" : "review-tag"}>{pair.delta === 0 ? "Matched" : "Method check"}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="source-ledger">
        <div className="table-head"><h3>Sources</h3></div>
        {sources.map((source) => {
          const sourceClaims = claims.filter((claim) => claim.sourceId === source.id);
          return (
            <article className={selectedSourceId === source.id ? "source-row is-highlighted" : "source-row"} key={source.id}>
              <span className="source-row__kind">{source.kind}</span>
              <div>
                <strong>{source.title}</strong>
                <small>{labById.get(source.publisherId)?.name} · {source.publishedAt}</small>
              </div>
              <span>{sourceClaims.length ? `${sourceClaims.length} claims` : "Pricing record"}</span>
              <a href={source.url} rel="noreferrer" target="_blank" aria-label={`Open ${source.title}`}>
                Source <Icon name="external" size={14} />
              </a>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function MethodologyView() {
  return (
    <section className="methodology-view">
      <div className="method-grid">
        <article><span>01</span><h3>Dated observations</h3></article>
        <article><span>02</span><h3>Owner ≠ reporter</h3></article>
        <article><span>03</span><h3>Protocol-specific</h3></article>
        <article><span>04</span><h3>Effective-dated prices</h3></article>
        <article><span>05</span><h3>Coverage disclosed</h3></article>
      </div>
    </section>
  );
}

export default App;
